import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, MonitorSmartphone, Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Field } from "@/pages/Login";

// Exchanges the Google session_id from the URL fragment, then continues into the app
export const AuthCallback = () => {
  const { google } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", location.pathname + location.search);
    google(sid).then((u) => { toast.success(`Welcome, ${u.first_name || u.name}.`); nav("/", { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); nav("/login", { replace: true }); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div data-testid="auth-callback" className="grid min-h-[60vh] place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
};

export const SessionEndedDialog = () => {
  const { sessionEnded, setSessionEnded } = useAuth();
  const nav = useNavigate();
  return (
    <AlertDialog open={sessionEnded}>
      <AlertDialogContent data-testid="session-ended-dialog" className="max-w-[calc(100%-2rem)] rounded-2xl sm:max-w-md">
        <AlertDialogHeader>
          <div className="mb-2 grid h-12 w-12 place-items-center rounded-full bg-amber-100 text-amber-700"><MonitorSmartphone className="h-6 w-6" /></div>
          <AlertDialogTitle className="font-display text-xl">Signed out on this device</AlertDialogTitle>
          <AlertDialogDescription>You have been logged out because your account was accessed on another device. Sign in again to continue here.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction data-testid="session-ended-login-btn" onClick={() => { setSessionEnded(false); nav("/login"); }} className="rounded-full bg-indigo-600 hover:bg-indigo-700">Go to sign in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const LeadCaptureModal = ({ open, onOpenChange, onDone }) => {
  const { captureLead } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone_number: "" });
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const g = await captureLead(f); toast.success(`You're in, ${g.first_name}! Enjoy the free demo.`); onOpenChange(false); onDone?.(); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-capture-modal" className="max-w-[calc(100%-2rem)] rounded-2xl bg-white sm:max-w-lg">
        <DialogHeader>
          <div className="mb-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700"><Sparkles className="h-3.5 w-3.5" />Free demo · no password</div>
          <DialogTitle className="font-display text-2xl">Start your free demo</DialogTitle>
          <DialogDescription>Tell us who you are and jump straight into sample questions from every section.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="lead-first-name-input" label="First name" autoComplete="given-name" value={f.first_name} onChange={set("first_name")} required />
            <Field id="lead-last-name-input" label="Last name" autoComplete="family-name" value={f.last_name} onChange={set("last_name")} required />
          </div>
          <Field id="lead-email-input" label="Email" type="email" autoComplete="email" value={f.email} onChange={set("email")} required />
          <Field id="lead-phone-input" label="Phone number" type="tel" autoComplete="tel" placeholder="+1 416 555 0100" value={f.phone_number} onChange={set("phone_number")} required />
          {err && <p data-testid="lead-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
          <button data-testid="lead-submit-btn" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Start free demo</button>
          <p className="text-center text-xs text-slate-400">Already have an account? <Link to="/login" onClick={() => onOpenChange(false)} data-testid="lead-signin-link" className="font-semibold text-indigo-600 hover:underline">Sign in</Link></p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const UpgradeLock = ({ title = "Free trial preview completed!", testId = "upgrade-lock" }) => (
  <div data-testid={testId} className="flex flex-col items-center justify-center gap-4 px-4 py-12 text-center">
    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-900 text-white shadow-lg"><Lock className="h-6 w-6" /></div>
    <h3 className="font-display text-2xl font-bold text-slate-900">{title}</h3>
    <p className="max-w-md text-slate-600">Unlock all full-length mock exams and question banks with a Practice Pass.</p>
    <Link to="/pricing" data-testid={`${testId}-upgrade-btn`} className="rounded-full bg-indigo-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700">Upgrade Now</Link>
  </div>
);
